import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { TenantFacadeService } from '@shared/features/tenant/shared/services/tenant-facade.service';
import {
  BaseGuardianshipSubscriberFromApi,
  GuardianshipSubscriber,
  GuardianshipSubscriberUpdate,
} from '../interfaces';
import { objectToCamel, ObjectToSnake } from 'ts-case-convert';
import { environment } from '@environments/environment';
import { SubscriberCreatePayloadSnake } from '@shared/interfaces';
import { GuardianshipSubscriberOverview } from '../interfaces/guardianship-subscriber-overview.interface';

@Injectable({
  providedIn: 'root',
})
export class SubscribersHttpService {
  private readonly _http = inject(HttpClient);

  private readonly _tenantService = inject(TenantFacadeService);

  private readonly _apiBaseUrl = computed(
    () => `${environment.api}/${this._tenantService.tenantApiPrefix()}`,
  );

  public updateSubscriber(
    appId: number,
    subscriberId: number,
    updateData: GuardianshipSubscriberUpdate,
  ): Observable<GuardianshipSubscriber> {
    return this._http
      .patch<BaseGuardianshipSubscriberFromApi>(
        `${this._apiBaseUrl()}/${appId}/subscribers/${subscriberId}/`,
        updateData,
      )
      .pipe(map((updatedSub) => objectToCamel(updatedSub)));
  }

  public getSubscribers(appId: number) {
    return this._http
      .get<
        GuardianshipSubscriberOverview[]
      >(`${this._apiBaseUrl()}/${appId}/subscribers/`)
      .pipe(
        map((subscribers) =>
          subscribers.map((subscriber) => objectToCamel(subscriber)),
        ),
      );
  }

  public getSubscriber(
    appId: number,
    subscriberId: number,
  ): Observable<GuardianshipSubscriber> {
    return this._http
      .get<
        ObjectToSnake<GuardianshipSubscriber>
      >(`${this._apiBaseUrl()}/${appId}/subscribers/${subscriberId}/`)
      .pipe(map((subscriber) => objectToCamel(subscriber)));
  }

  public deleteSubscriber(
    appId: number,
    subscriberId: number,
  ): Observable<null> {
    return this._http.delete<null>(
      `${this._apiBaseUrl()}/${appId}/subscribers/${subscriberId}/`,
    );
  }

  public createSubscriber(
    appId: number,
    createData: SubscriberCreatePayloadSnake,
  ): Observable<GuardianshipSubscriber> {
    return this._http
      .post<
        ObjectToSnake<GuardianshipSubscriber>
      >(`${this._apiBaseUrl()}/${appId}/subscribers/`, createData)
      .pipe(map((subscriber) => objectToCamel(subscriber)));
  }
}
